import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/** URL parameter for the search term. facets.js already keeps `q` when other filters change. */
const SEARCH_PARAM = 'q';

/** Dispatched on document when the search term changes, so desktop and mobile inputs stay in sync. */
const SEARCH_CHANGE_EVENT = 'collection-search:change';

/** Dispatched by assets/gift-filter.js; the empty state depends on both filters. */
const GIFT_CHANGE_EVENT = 'gift-filter:change';

/** The <style> element in <head> that hides non-matching cards. */
const STYLE_ID = 'collection-search-style';

/**
 * Normalises text for matching: lowercase, accents removed, collapsed whitespace.
 * @param {string} text
 * @returns {string}
 */
const normalize = (text) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/** @returns {string} The search term from the URL. */
const getQuery = () => new URL(window.location.href).searchParams.get(SEARCH_PARAM) ?? '';

/**
 * Collection search: filters the product cards on the current page as the user types.
 *
 * Each card has a `data-search-text` attribute (title + configured metafields, see
 * snippets/product-search-text.liquid). Matching cards stay visible through a <style> rule in <head>,
 * so section re-renders (filters, sorting) and infinite scroll don't reset it; the rule is rebuilt
 * whenever cards change. The URL (?q=) is the source of truth.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} input - The search input.
 *
 * @extends {Component<Refs>}
 */
class CollectionSearchComponent extends Component {
  requiredRefs = ['input'];

  /** @type {HTMLElement | null} */
  #section = null;

  /** @type {MutationObserver | undefined} */
  #sectionObserver;

  /** @type {number | undefined} */
  #syncFrame;

  connectedCallback() {
    super.connectedCallback();

    this.#sync();

    window.addEventListener('popstate', this.#scheduleSync);
    document.addEventListener(SEARCH_CHANGE_EVENT, this.#scheduleSync);
    document.addEventListener(GIFT_CHANGE_EVENT, this.#scheduleSync);

    // Cards change on re-render and infinite scroll; "Clear all" drops ?q= from the URL on click.
    this.#section = this.closest('.shopify-section');
    if (this.#section) {
      this.#section.addEventListener('click', this.#onSectionEvent);
      this.#section.addEventListener('keydown', this.#onSectionEvent);
      this.#sectionObserver = new MutationObserver(this.#scheduleSync);
      this.#sectionObserver.observe(this.#section, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('popstate', this.#scheduleSync);
    document.removeEventListener(SEARCH_CHANGE_EVENT, this.#scheduleSync);
    document.removeEventListener(GIFT_CHANGE_EVENT, this.#scheduleSync);
    this.#section?.removeEventListener('click', this.#onSectionEvent);
    this.#section?.removeEventListener('keydown', this.#onSectionEvent);
    this.#sectionObserver?.disconnect();
    this.handleInput.cancel();
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);
  }

  /** Filters as the user types. */
  handleInput = debounce(() => this.#updateURL(), 200);

  /**
   * Keeps the search on the page instead of submitting to /search.
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();
    this.handleInput.cancel();
    this.#updateURL();
  }

  /** Writes the input value to ?q= (replaceState, so typing doesn't flood the history). */
  #updateURL() {
    const url = new URL(window.location.href);
    const query = this.refs.input.value.trim();

    if (query) {
      url.searchParams.set(SEARCH_PARAM, query);
    } else {
      url.searchParams.delete(SEARCH_PARAM);
    }
    url.searchParams.delete('page');

    if (url.href !== window.location.href) {
      history.replaceState(history.state, '', url.toString());
    }
    document.dispatchEvent(new CustomEvent(SEARCH_CHANGE_EVENT));
  }

  /**
   * Re-syncs after clicks/keys elsewhere in the section (e.g. "Clear all"), not while typing here.
   * @param {Event} event
   */
  #onSectionEvent = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#scheduleSync();
  };

  /** Batches sync calls from mutations/events into one per frame. */
  #scheduleSync = () => {
    if (this.#syncFrame) return;

    this.#syncFrame = requestAnimationFrame(() => {
      this.#syncFrame = undefined;
      this.#sync();
    });
  };

  /** Mirrors ?q= onto the input (unless the user is typing in it) and applies the filter. */
  #sync() {
    const query = getQuery();
    const { input } = this.refs;

    if (input && document.activeElement !== input && input.value !== query) {
      input.value = query;
    }

    this.#apply(query);
  }

  /**
   * Shows only the cards whose search text contains every word of the query.
   * @param {string} query
   */
  #apply(query) {
    const terms = normalize(query).split(' ').filter(Boolean);
    let style = /** @type {HTMLStyleElement | null} */ (document.getElementById(STYLE_ID));

    // Lets CSS react to an active search, e.g. showing "Clear all"
    const root = document.documentElement;

    if (terms.length === 0) {
      style?.remove();
      if ('collectionSearch' in root.dataset) delete root.dataset.collectionSearch;
      return;
    }

    if (!('collectionSearch' in root.dataset)) root.dataset.collectionSearch = '';

    const cards = /** @type {NodeListOf<HTMLElement>} */ (
      (this.#section ?? document).querySelectorAll('.product-grid__item[data-product-id]')
    );

    /** @type {Set<string>} */
    const matchingIds = new Set();
    for (const card of cards) {
      const text = normalize(card.dataset.searchText ?? '');
      if (terms.every((term) => text.includes(term))) matchingIds.add(card.dataset.productId ?? '');
    }

    const selector = [...matchingIds].map((id) => `[data-product-id="${CSS.escape(id)}"]`).join(', ');
    const css = selector
      ? `.product-grid__item[data-product-id]:not(${selector}) { display: none; }`
      : '.product-grid__item[data-product-id] { display: none; }';

    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.append(style);
    }
    if (style.textContent !== css) style.textContent = css;

    // Empty state: checked on the next frame so the Gifts filter (if toggled together) is applied too
    const searchStyle = style;
    requestAnimationFrame(() => {
      const hasVisibleCard = [...cards].some((card) => getComputedStyle(card).display !== 'none');
      searchStyle.textContent = hasVisibleCard ? css : `${css} .facets-search-empty { display: block; }`;
    });
  }
}

if (!customElements.get('collection-search-component')) {
  customElements.define('collection-search-component', CollectionSearchComponent);
}
